import { join, lowerCase, map as mapCapped, replace, split } from 'lodash/fp';
import dayjs from 'dayjs';
import React from 'react';
import { Button, Grid, Stack, Typography } from '@mui/material';
import {
  AirplanemodeActiveTwoTone,
  FlightLandTwoTone,
  LocalPhoneTwoTone,
  PermPhoneMsgTwoTone,
  PreviewTwoTone,
  RoomTwoTone,
} from '@mui/icons-material';
import { Caption } from './material-theme';
import { StyledButtonGrid } from './assests/style';
import { AddToCalendar } from './AddToCalendarButton';
import { SignUpButton, WithdrawButton } from './SignupButton';
import { toLocaleDate, toLocaleDateShort } from './util/to-local-date';
import { useHistory } from 'react-router-dom';

const map = mapCapped.convert({ cap: false });

const requestTitle = 'Airport Pickup';

const convertTo12Hour = time => {
    if (/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/.test(time)) {
        const [hour, minute] = time.split(':');
        const h = parseInt(hour, 10);
        return `${(h % 12) || 12}:${minute} ${h < 12 ? 'AM' : 'PM'}`;
    }
    return time;
};

const AirportPickup = ({
  request: {
    Id,
    Arrival_Date__c,
    Arrival_Time__c,
    Airport__c,
    Airline__c,
    Flight_Number__c,
    From_City__c,
    Airport_Ride_Friends__c,
    Address_for_drop_off__c,
    Event__r,
    Contact,
    signUpInfo: {
      isSignedUp,
      canSignUpAirportPickup,
      isSignedUpAirportPickup,
      isHistoryRecord,
      isPendingApproval,
      genderCheck
    },
    Volunteer__r,
    Volunteer_Phone__c,
  },
  actionPerform,
  requestType,
}) => {
  const isEvent = requestType === 'Event';
  const history = useHistory();

  const setPayloadForCalendar = () => {
    const setEventDate = (dateType) => {
      const isPM = Arrival_Time__c.slice(-2) === 'PM';
      const [hh, mm] = Arrival_Time__c.slice(0, -2).trim().split(':');
      const time = `${isPM ? Number(hh) + 12 : hh}:${mm}:00`;

      if (dateType === 'end') {
        return dayjs(`${Arrival_Date__c}T${time}`)
          .add(15, 'minute')
          .format('YYYYMMDDTHHmmss');
      }
      return dayjs(`${Arrival_Date__c}T${time}`).format('YYYYMMDDTHHmmss');
    };

    return {
      summary: `${Contact?.Name} ${requestTitle}`,
      description: `View in IFI Volunteer App.
      ${window.location.href}#_${Id}`,
      location: Airport__c,
      startDate: setEventDate('start'),
      endDate: setEventDate('end'),
      dstamp: dayjs().format('YYYYMMDDTHHmmss'),
    };
  };

  const airportAction =
    (actionUrl, isSignUpRequest = false) =>
    () => {
      const requestName = isEvent ? 'events' : 'requests';

      const apiUrl = `/api/${requestName}/airport-pickup/${actionUrl}`;

      const params = { requestId: Id };
      if (isEvent) {
        params.eventId = Event__r.Id;
      }

      return actionPerform(apiUrl, params, isSignUpRequest);
    };

  const signedUpDetails = [
    {
      caption: 'Flight',
      body: Flight_Number__c,
      customContent: (
        <Button
          variant="outlined"
          size="small"
          endIcon={<AirplanemodeActiveTwoTone />}
          target="_blank"
          href={`https://www.google.com/search?q=flight+status+${Flight_Number__c}`}
        >
          Track
        </Button>
      ),
    },
    {
      caption: 'Carrier',
      body: Airline__c,
    },
    {
      caption: 'From',
      body: From_City__c,
    },
    {
      caption: 'To',
      body: Airport__c,
    },
    {
      caption: 'Drop Off Location',
      body: Address_for_drop_off__c,
      customContent: Address_for_drop_off__c && (
        <Button
          variant="outlined"
          size="small"
          endIcon={<RoomTwoTone />}
          target="_blank"
          href={`https://www.google.com/maps/place/${join(
            '+',
            split(' ', join(' ', split('\r\n', Address_for_drop_off__c))),
          )}`}
        >
          Map
        </Button>
      ),
      spaceCount: { xs: 12 },
    },
  ];

  const layout = [
    {
      caption: 'Arrives',
      body: `${toLocaleDate(Arrival_Date__c)} ${convertTo12Hour(Arrival_Time__c) || ''}`,
      spaceCount: { xs: 12 },
    },
  ];

  if (Airport_Ride_Friends__c) {
    layout.splice(0, 0, {
      caption: 'Pick up Friends',
      body: Airport_Ride_Friends__c,
      spaceCount: { xs: 12 },
    });
  }

  if (isSignedUp && !isPendingApproval) {
    signedUpDetails.forEach((detail) => layout.push(detail));
  }

  if (
    !isSignedUpAirportPickup &&
    !canSignUpAirportPickup &&
    !isPendingApproval
  ) {
    layout.push({
      caption: 'Volunteer',
      body: Volunteer__r?.Name,
      customContent:
        isSignedUp &&
        !isSignedUpAirportPickup &&
        (Volunteer_Phone__c ? (
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              size="small"
              target="blank"
              href={`tel:${Volunteer__r?.MobilePhone}`}
              endIcon={<LocalPhoneTwoTone />}
            >
              Call
            </Button>
            <Button
              variant="outlined"
              size="small"
              target="blank"
              href={`sms:${Volunteer__r?.MobilePhone}`}
              endIcon={<PermPhoneMsgTwoTone />}
            >
              Text
            </Button>
          </Stack>
        ) : (
          <Typography variant="caption">
            Phone number is not available
          </Typography>
        )),
      spaceCount: { xs: 12 },
    });
  }

  const getActionButtons = () => {
    const buttons = [];
    const confirmHeader = `${requestTitle} ${toLocaleDateShort(
      Arrival_Date__c,
    )}`;

    if (canSignUpAirportPickup && genderCheck && !isHistoryRecord) {
      buttons.push({
        spaceCount: { xs: 12 },
        btn: (
          <SignUpButton
            onClick={airportAction('signup', true)}
            Icon={<FlightLandTwoTone />}
            confirmHeader={confirmHeader}
          />
        ),
      });
    }
    if (isSignedUpAirportPickup && !isHistoryRecord) {
      buttons.push({
        spaceCount: { xs: 12 },
        btn: (
          <WithdrawButton
            onClick={airportAction('withdraw')}
            confirmHeader={confirmHeader}
          />
        ),
      });
      if (!isPendingApproval) {
        buttons.push({
          spaceCount: { xs: 6 },
          btn: (
            <AddToCalendar
              getPayload={setPayloadForCalendar}
              fileName={`ifi-${replace(
                ' ',
                '-',
                lowerCase(requestTitle),
              )}-${Id}`}
            />
          ),
        });
        buttons.push({
          spaceCount: { xs: 6 },
          btn: (
            <Button
              variant="contained"
              startIcon={<PreviewTwoTone />}
              onClick={() =>
                history.push({
                  pathname: '/welcome',
                  state: {
                    StudentName: Contact?.Name,
                    isPendingApproval,
                  },
                })
              }
            >
              Sign
            </Button>
          ),
        });
      }
    }
    return buttons;
  };

  const actionButtons = getActionButtons();

  return (
    !Arrival_Date__c || (
      <>
        <Grid container spacing={0.5}>
          <Grid item xs={12}>
            <Typography variant="h5">
              <FlightLandTwoTone /> {requestTitle}
            </Typography>
          </Grid>
          {map(
            (
              { caption, body, customContent, spaceCount = { xs: 6 } },
              index,
            ) => (
              <Grid item {...spaceCount} key={index}>
                <Caption>{caption}</Caption>
                <Typography>{body}</Typography>
                {customContent}
              </Grid>
            ),
            layout,
          )}
          {map(
            ({ spaceCount, btn }, index) => (
              <StyledButtonGrid item {...spaceCount} key={index}>
                {btn}
              </StyledButtonGrid>
            ),
            actionButtons,
          )}
        </Grid>
      </>
    )
  );
};

export { AirportPickup };
